import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";

import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Snackbar from "@material-ui/core/Snackbar";

import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SendIcon from "@material-ui/icons/Send";

const basePath =
  process.env.NODE_ENV === "production" ? "/edge" : "http://localhost:3010";

const STATUS_COLORS = {
  open: "#2196f3",
  in_progress: "#ff9800",
  waiting_on_player: "#9c27b0",
  resolved: "#4caf50",
  closed: "#757575",
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#1C1E20",
    minHeight: "100vh",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: "#ffffff",
    flex: 1,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 320px",
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  card: {
    backgroundColor: "#262829",
    borderRadius: 12,
    padding: theme.spacing(3),
  },
  label: {
    fontSize: "0.78rem",
    fontWeight: 700,
    color: "rgba(255,255,255,0.5)",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(0.5),
  },
  value: {
    color: "#ffffff",
    marginBottom: theme.spacing(2),
  },
  messageBubble: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    marginBottom: theme.spacing(1.5),
    maxWidth: "85%",
  },
  playerMessage: {
    backgroundColor: "rgba(255,255,255,0.06)",
    alignSelf: "flex-start",
  },
  adminMessage: {
    backgroundColor: "rgba(255,117,0,0.12)",
    alignSelf: "flex-end",
    marginLeft: "auto",
  },
  historyItem: {
    padding: theme.spacing(1, 0),
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    "&:last-child": { borderBottom: "none" },
  },
}));

export default function SupportTicketDetailPage({ catalog }) {
  const classes = useStyles();
  const history = useHistory();
  const { ticketId } = useParams();
  const user = catalog?.user;

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState("");
  const [replying, setReplying] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState("");
  const [priorityUpdate, setPriorityUpdate] = useState("");
  const [snackbar, setSnackbar] = useState("");

  const fetchTicket = async () => {
    if (!user?.userId) return;
    setLoading(true);
    try {
      const resp = await fetch(
        `${basePath}/api/v7/admin/support-tickets/${ticketId}/adpass/${user.userId}`,
        { credentials: "include" }
      );
      const data = await resp.json();
      if (data.ok) {
        setTicket(data.ticket);
        setStatusUpdate(data.ticket.status);
        setPriorityUpdate(data.ticket.priority);
      }
    } catch (err) {
      console.error("Failed to fetch ticket", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicket();
  }, [ticketId, user?.userId]);

  const handleReply = async () => {
    if (!replyContent.trim()) return;
    setReplying(true);
    try {
      const resp = await fetch(
        `${basePath}/api/v7/admin/support-tickets/${ticketId}/reply/adpass/${user.userId}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content: replyContent.trim() }),
        }
      );
      const data = await resp.json();
      if (data.ok) {
        setReplyContent("");
        setSnackbar("Reply sent");
        fetchTicket();
      }
    } catch (err) {
      console.error("Failed to reply", err);
    } finally {
      setReplying(false);
    }
  };

  const handleStatusUpdate = async () => {
    try {
      const body = {};
      if (statusUpdate && statusUpdate !== ticket.status) body.status = statusUpdate;
      if (priorityUpdate && priorityUpdate !== ticket.priority) body.priority = priorityUpdate;
      if (!Object.keys(body).length) return;

      const resp = await fetch(
        `${basePath}/api/v7/admin/support-tickets/${ticketId}/update-status/adpass/${user.userId}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      const data = await resp.json();
      if (data.ok) {
        setSnackbar("Ticket updated");
        fetchTicket();
      }
    } catch (err) {
      console.error("Failed to update ticket", err);
    }
  };

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className={classes.root}>
        <Box display="flex" justifyContent="center" py={10}>
          <CircularProgress style={{ color: "#FF7500" }} />
        </Box>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className={classes.root}>
        <Typography style={{ color: "#fff" }}>Ticket not found.</Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <IconButton onClick={() => history.push("/admin/support-tickets")} style={{ color: "#fff" }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography className={classes.title}>
          {ticket.ticketNumber} &mdash; {ticket.subject}
        </Typography>
        <Chip
          label={ticket.status?.replace(/_/g, " ")}
          style={{
            backgroundColor: STATUS_COLORS[ticket.status] || "#757575",
            color: "#fff",
            fontWeight: 600,
            textTransform: "capitalize",
          }}
        />
      </div>

      <div className={classes.grid}>
        <div>
          <Paper className={classes.card} style={{ marginBottom: 24 }}>
            <Typography className={classes.label}>Description</Typography>
            <Typography className={classes.value} style={{ whiteSpace: "pre-wrap" }}>
              {ticket.description}
            </Typography>

            <Box display="flex" gap={3} flexWrap="wrap">
              <div>
                <Typography className={classes.label}>Category</Typography>
                <Typography className={classes.value} style={{ textTransform: "capitalize" }}>
                  {ticket.category}
                </Typography>
              </div>
              <div>
                <Typography className={classes.label}>Submitter</Typography>
                <Typography className={classes.value}>
                  {ticket.nickname || "Guest"} ({ticket.email})
                </Typography>
              </div>
              <div>
                <Typography className={classes.label}>Created</Typography>
                <Typography className={classes.value}>{formatDate(ticket.createdAt)}</Typography>
              </div>
            </Box>
          </Paper>

          <Paper className={classes.card}>
            <Typography className={classes.label} style={{ marginBottom: 16 }}>
              Conversation ({(ticket.messages || []).length} messages)
            </Typography>

            <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
              {(ticket.messages || []).length === 0 && (
                <Typography style={{ color: "rgba(255,255,255,0.4)", fontStyle: "italic" }}>
                  No messages yet.
                </Typography>
              )}
              {(ticket.messages || []).map((msg, i) => (
                <div
                  key={i}
                  className={`${classes.messageBubble} ${
                    msg.author === "admin" ? classes.adminMessage : classes.playerMessage
                  }`}
                >
                  <Typography
                    style={{
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      color: msg.author === "admin" ? "#FF7500" : "rgba(255,255,255,0.5)",
                      marginBottom: 4,
                    }}
                  >
                    {msg.authorName} &middot; {formatDate(msg.timestamp)}
                  </Typography>
                  <Typography style={{ color: "#fff", whiteSpace: "pre-wrap", fontSize: "0.95rem" }}>
                    {msg.content}
                  </Typography>
                </div>
              ))}
            </Box>

            <Box display="flex" gap={1} mt={2} alignItems="flex-end">
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                variant="outlined"
                placeholder="Write a reply..."
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                InputProps={{ style: { color: "#fff" } }}
              />
              <IconButton
                onClick={handleReply}
                disabled={replying || !replyContent.trim()}
                style={{ color: "#FF7500" }}
              >
                {replying ? <CircularProgress size={20} /> : <SendIcon />}
              </IconButton>
            </Box>
          </Paper>
        </div>

        <div>
          <Paper className={classes.card} style={{ marginBottom: 24 }}>
            <Typography className={classes.label} style={{ marginBottom: 12 }}>
              Manage
            </Typography>

            <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: 16 }}>
              <InputLabel style={{ color: "rgba(255,255,255,0.5)" }}>Status</InputLabel>
              <Select
                value={statusUpdate}
                onChange={(e) => setStatusUpdate(e.target.value)}
                label="Status"
                style={{ color: "#fff" }}
              >
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="waiting_on_player">Waiting on Player</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: 16 }}>
              <InputLabel style={{ color: "rgba(255,255,255,0.5)" }}>Priority</InputLabel>
              <Select
                value={priorityUpdate}
                onChange={(e) => setPriorityUpdate(e.target.value)}
                label="Priority"
                style={{ color: "#fff" }}
              >
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              fullWidth
              onClick={handleStatusUpdate}
              style={{ backgroundColor: "#FF7500", color: "#111", fontWeight: 700 }}
            >
              Update ticket
            </Button>
          </Paper>

          <Paper className={classes.card}>
            <Typography className={classes.label} style={{ marginBottom: 12 }}>
              Status History
            </Typography>
            {(ticket.statusHistory || []).length === 0 ? (
              <Typography style={{ color: "rgba(255,255,255,0.4)", fontStyle: "italic", fontSize: "0.9rem" }}>
                No status changes yet.
              </Typography>
            ) : (
              (ticket.statusHistory || []).map((entry, i) => (
                <div key={i} className={classes.historyItem}>
                  <Typography style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem" }}>
                    {formatDate(entry.timestamp)}
                  </Typography>
                  <Typography style={{ color: "#fff", fontSize: "0.9rem" }}>
                    {entry.adminName}: {entry.from} &rarr; {entry.to}
                  </Typography>
                </div>
              ))
            )}
          </Paper>
        </div>
      </div>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar("")}
        message={snackbar}
      />
    </div>
  );
}

SupportTicketDetailPage.propTypes = {
  catalog: PropTypes.object.isRequired,
};
